import React from 'react';
import { Link } from 'react-router-dom';
import { CheckIcon, ShoppingCartIcon, AlertCircleIcon, CreditCardIcon, ShieldIcon, UserIcon } from 'lucide-react';
import { formatCurrency } from './utils';
import { useDarkMode } from '../../context/DarkModeContext';
import type { BookBox } from './types';
interface SubscribeTabProps {
  bookBox: BookBox;
  subscriptionLength: string;
  setSubscriptionLength: React.Dispatch<React.SetStateAction<string>>;
  selectedOption: string | null;
  setSelectedOption: React.Dispatch<React.SetStateAction<string | null>>;
}
export const SubscribeTab: React.FC<SubscribeTabProps> = ({
  bookBox,
  subscriptionLength,
  setSubscriptionLength,
  selectedOption,
  setSelectedOption
}) => {
  const {
    darkMode
  } = useDarkMode();
  const plans = [{
    id: '1 Box',
    label: 'Monthly',
    price: formatCurrency(bookBox.price, bookBox.currency),
    originalPrice: null,
    savings: null,
    billing: 'Billed monthly',
    perks: ['One box per month', 'Cancel anytime', 'Skip any month'],
    badge: null
  }, {
    id: '3 Boxes',
    label: '3-Month',
    price: formatCurrency((parseFloat(bookBox.price) * 0.9).toFixed(2), bookBox.currency),
    originalPrice: formatCurrency(bookBox.price, bookBox.currency),
    savings: 'Save 10%',
    billing: 'Billed every 3 months',
    perks: ['One box per month', '10% discount', 'Skip any month'],
    badge: null
  }, {
    id: '6 Boxes',
    label: '6-Month',
    price: formatCurrency((parseFloat(bookBox.price) * 0.85).toFixed(2), bookBox.currency),
    originalPrice: formatCurrency(bookBox.price, bookBox.currency),
    savings: 'Save 15%',
    billing: 'Billed every 6 months',
    perks: ['One box per month', '15% discount', 'Free bonus item'],
    badge: 'Best Value'
  }];
  return <div data-id="element-7674" data-mp="cuuz3bnscxg">
      <h2 className={`text-2xl font-bold mb-6 ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-7675" data-mp="b0a1p0e0ktd">
        Subscribe to {bookBox.boxName}
      </h2>

      <div className={`border rounded-lg p-6 shadow-sm ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`} data-id="element-7676" data-mp="24fwellbdomd7">
        {/* Subscription Plans */}
        <div className="mb-6" data-id="element-7677" data-mp="298piy82xpsor">
          <h3 className={`text-xl font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-7678" data-mp="2e68xza7gi9hr">
            Subscription Options
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-7679" data-mp="1gkbkjr7xouco">
            {plans.map(plan => {
            const isSelected = subscriptionLength === plan.id;
            return <div key={plan.id} className={`border rounded-lg p-4 transition-colors ${isSelected ? darkMode ? 'border-purple-500 bg-purple-900/20' : 'border-indigo-500 bg-indigo-50' : darkMode ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200'}`} data-id="element-7680" data-mp="1qi7w2k4s9rbb">
                  <div className="flex justify-between items-start mb-2" data-id="element-7681" data-mp="36w7pdc94iipl">
                    <h4 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-7682" data-mp="v689yfgjv5ax">
                      {plan.label}
                    </h4>
                    {isSelected ? <span className={`text-xs px-2 py-1 rounded-full ${darkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-indigo-100 text-indigo-800'}`} data-id="element-7683" data-mp="gin9ktb27k6p">
                        Selected
                      </span> : plan.badge ? <span className={`text-xs px-2 py-1 rounded-full ${darkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-800'}`} data-id="element-7684" data-mp="2hucc0tdiazw">
                        {plan.badge}
                      </span> : null}
                  </div>

                  <div className="flex items-baseline mb-2" data-id="element-7685" data-mp="1rybxbmw50n8k">
                    <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-7686" data-mp="pjw5cj05188l">
                      {plan.price}
                    </p>
                    {plan.originalPrice && <p className={`text-sm line-through ml-2 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`} data-id="element-7687" data-mp="fl6omtjz9ttx">
                        {plan.originalPrice}
                      </p>}
                  </div>

                  {plan.savings && <p className="text-sm text-green-500 font-medium mb-1" data-id="element-7688" data-mp="anwtcx9wqny6">
                      {plan.savings}
                    </p>}
                  <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-7689" data-mp="1tif8y145jgn">
                    {plan.billing}
                  </p>

                  <ul className="space-y-2 mb-4" data-id="element-7690" data-mp="3o1rpfn2ghgly">
                    {plan.perks.map(perk => <li key={perk} className="flex items-start" data-id="element-7691" data-mp="34eya7xf4ecqe">
                        <CheckIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" data-id="element-7692" data-mp="30e4lx2xrmvwz" />
                        <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-7693" data-mp="30ghc23dogcl4">
                          {perk}
                        </span>
                      </li>)}
                  </ul>

                  <button onClick={() => setSubscriptionLength(plan.id)} className={`w-full py-2 rounded-md font-medium transition-colors ${isSelected ? 'bg-purple-600 hover:bg-purple-700 text-white' : darkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`} data-id="element-7694" data-mp="3nchl76l525fg">
                    {isSelected ? 'Selected' : 'Select'}
                  </button>
                </div>;
          })}
          </div>
        </div>

        {/* Customization Options */}
        {bookBox.customizationOptions && bookBox.customizationOptions.length > 0 && <div className="mb-6" data-id="element-7695" data-mp="i15ctws868rc">
              <h3 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-7696" data-mp="hyapnxhtu9u8">
                Customization Options
              </h3>
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`} data-id="element-7697" data-mp="rs9r0ua2tpl4">
                <div className="space-y-3" data-id="element-7698" data-mp="10qlfoqrdudax">
                  {bookBox.customizationOptions.map((option, index) => <div key={index} className="flex items-center" data-id="element-7699" data-mp="15pol4cb9yljf" data-mpi={index}>
                      <input type="radio" id={`option-${index}`} name="customization" checked={selectedOption === option} onChange={() => setSelectedOption(option)} className="h-4 w-4 text-purple-600 border-gray-300 focus:ring-purple-500" data-id="element-7700" data-mp="1ap16stqetmkf" />
                      <label htmlFor={`option-${index}`} className={`ml-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-7701" data-mp="306gag94a4wrr">
                        {option}
                      </label>
                    </div>)}
                </div>
              </div>
            </div>}

        {/* Content Preferences */}
        <div className="mb-6" data-id="element-7702" data-mp="3iwxr3fbu81wr">
          <h3 className={`text-lg font-semibold mb-3 flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-7703" data-mp="3ixzbwbino2ro">
            <ShieldIcon className={`h-5 w-5 mr-2 ${darkMode ? 'text-purple-400' : 'text-indigo-500'}`} data-id="element-7704" data-mp="3dzecuff53p4t" />
            Content Preferences
          </h3>
          <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`} data-id="element-7705" data-mp="22jpn5gai4c0b">
            <p className={`text-sm mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-7706" data-mp="1xlfouwthcvah">
              We respect your reading preferences. You can set content warnings
              and preferences in your account settings.
            </p>
            <Link to="/profile-settings" className={`inline-flex items-center text-sm font-medium ${darkMode ? 'text-purple-400 hover:text-purple-300' : 'text-indigo-600 hover:text-indigo-800'}`} data-id="element-7707" data-mp="2e8d97l4efqye">
              <UserIcon className="h-4 w-4 mr-1.5" data-id="element-7708" data-mp="3ax1pod0gv65p" />
              Manage Content Preferences
            </Link>
          </div>
        </div>

        {/* Manage Subscriptions */}
        <div className="mb-6" data-id="element-7709" data-mp="2x5eivj8wef8o">
          <h3 className={`text-lg font-semibold mb-3 flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-7710" data-mp="317001gsgtfoo">
            <CreditCardIcon className={`h-5 w-5 mr-2 ${darkMode ? 'text-purple-400' : 'text-indigo-500'}`} data-id="element-7711" data-mp="3ow146ru9m4o5" />
            Manage Your Subscriptions
          </h3>
          <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`} data-id="element-7712" data-mp="1i7im5gkyvvlb">
            <p className={`text-sm mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-7713" data-mp="1d8xvo8vioqa2">
              Once subscribed, you can manage all your book box subscriptions
              from your account settings.
            </p>
            <Link to="/settings/subscriptions" className={`inline-flex items-center text-sm font-medium ${darkMode ? 'text-purple-400 hover:text-purple-300' : 'text-indigo-600 hover:text-indigo-800'}`} data-id="element-7714" data-mp="z9g0nbrmogs9">
              <UserIcon className="h-4 w-4 mr-1.5" data-id="element-7715" data-mp="199gonb0fzl2s" />
              View My Subscriptions
            </Link>
          </div>
        </div>

        {/* Checkout */}
        <div className="mt-6" data-id="element-7716" data-mp="2kmj4g3upw6lw">
          <button className="w-full bg-purple-600 hover:bg-purple-700 text-white font-medium py-3 px-4 rounded-md flex items-center justify-center transition-colors" data-id="element-7717" data-mp="2klpsmogwiqww">
            <ShoppingCartIcon className="h-5 w-5 mr-2" data-id="element-7718" data-mp="323dh8qlnpqgw" />
            Subscribe Now
          </button>
          <div className="mt-4 flex items-start" data-id="element-7719" data-mp="2x393h8c4v9ud">
            <AlertCircleIcon className={`h-5 w-5 mr-2 flex-shrink-0 mt-0.5 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`} data-id="element-7720" data-mp="2x4kd3os9fbte" />
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7721" data-mp="2ner5szz5hiuy">
              100% Satisfaction Guarantee. Cancel or pause your subscription
              anytime before your next billing date.
            </p>
          </div>
        </div>
      </div>
    </div>;
};